import { useState, useEffect } from "react"
import { useQuery, useMutation } from "urql"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2, Pencil, Upload, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { JsonViewer } from "@/components/shared/json-viewer"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { sectionTitleClass } from "@/components/shared/field-label"
import { EventTypeSchemasQuery } from "@/lib/graphql/queries"
import {
  CreateEventTypeSchemaMutation,
  UpdateEventTypeSchemaMutation,
  DeleteEventTypeSchemaMutation,
  ImportEventTypesFromOpenAPIMutation,
} from "@/lib/graphql/mutations"
import { eventTypeSchemaFormSchema, type EventTypeSchemaFormValues } from "@/lib/schemas"
import type { ResultOf } from "@/lib/graphql/types"

type SchemaItem = ResultOf<typeof EventTypeSchemasQuery>["eventTypeSchemas"]["nodes"][number]

const LIMIT = 25

function parseJSON(str: string): Record<string, unknown> | undefined {
  if (!str.trim()) return undefined
  try { return JSON.parse(str) } catch { return undefined }
}

export function EventTypesPage() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [search])

  const [result, reexecute] = useQuery({
    query: EventTypeSchemasQuery,
    variables: { search, after: pagination.after, first: pagination.first },
  })
  const [, createSchema] = useMutation(CreateEventTypeSchemaMutation)
  const [, updateSchema] = useMutation(UpdateEventTypeSchemaMutation)
  const [, deleteSchema] = useMutation(DeleteEventTypeSchemaMutation)

  const [, importFromOpenAPI] = useMutation(ImportEventTypesFromOpenAPIMutation)

  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editItem, setEditItem] = useState<SchemaItem | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const handleCreate = async (form: EventTypeSchemaFormValues) => {
    const res = await createSchema({
      input: {
        eventType: form.eventType,
        description: form.description || undefined,
        schema: parseJSON(form.schema),
        example: parseJSON(form.example),
      },
    })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Event type schema created")
    setCreateOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleUpdate = async (form: EventTypeSchemaFormValues) => {
    if (!editItem) return
    const res = await updateSchema({
      id: editItem.id,
      input: {
        eventType: form.eventType,
        description: form.description,
        schema: parseJSON(form.schema),
        example: parseJSON(form.example),
      },
    })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Event type schema updated")
    setEditItem(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleDelete = async () => {
    if (!deleteId) return
    const res = await deleteSchema({ id: deleteId })
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Event type schema deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const selectedItem = expandedId
    ? (result.data?.eventTypeSchemas.nodes ?? []).find((n) => n.id === expandedId) ?? null
    : null

  const columns: Column<SchemaItem>[] = [
    {
      header: "Event Type",
      cell: (row) => (
        <span className="text-xs font-mono font-medium">{row.eventType}</span>
      ),
    },
    {
      header: "Description",
      cell: (row) => (
        <span className="text-xs text-muted-foreground max-w-80 truncate block">
          {row.description || "\u2014"}
        </span>
      ),
    },
    {
      header: "Schema",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">
          {row.schema ? "Defined" : "\u2014"}
        </span>
      ),
    },
    {
      header: "Updated",
      cell: (row) => <TimeAgo date={row.updatedAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setEditItem(row) }}
            aria-label="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Event Type Catalogue" description="Schemas and examples for event types">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open("/catalog/default", "_blank")}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Public Catalog
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Import OpenAPI
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="shadow-primary-glow transition-shadow">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Schema
          </Button>
        </div>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Input
          placeholder="Search event types..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <DataTable
        columns={columns}
        data={result.data?.eventTypeSchemas.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.eventTypeSchemas.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No event type schemas"
        emptyDescription="Define schemas to document your webhook event types"
        onRowClick={(row) => setExpandedId(expandedId === row.id ? null : row.id)}
      />

      {selectedItem && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              <code className="font-mono">{selectedItem.eventType}</code>
            </CardTitle>
            {selectedItem.description && (
              <p className="text-sm text-muted-foreground">{selectedItem.description}</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedItem.schema && (
                <div>
                  <div className="text-xs font-medium mb-2 text-muted-foreground">JSON Schema</div>
                  <JsonViewer data={selectedItem.schema} />
                </div>
              )}
              {selectedItem.example && (
                <div>
                  <div className="text-xs font-medium mb-2 text-muted-foreground">Example Payload</div>
                  <JsonViewer data={selectedItem.example} />
                </div>
              )}
              {!selectedItem.schema && !selectedItem.example && (
                <p className="text-sm text-muted-foreground col-span-2">No schema or example defined.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <SchemaFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Create Event Type Schema"
        onSubmit={handleCreate}
      />

      {editItem && (
        <SchemaFormDialog
          open
          onOpenChange={() => setEditItem(null)}
          title="Edit Event Type Schema"
          initial={{
            eventType: editItem.eventType,
            description: editItem.description,
            schema: editItem.schema ? JSON.stringify(editItem.schema, null, 2) : "",
            example: editItem.example ? JSON.stringify(editItem.example, null, 2) : "",
          }}
          onSubmit={handleUpdate}
        />
      )}

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Event Type Schema"
        description="This will permanently delete this event type schema."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />

      <ImportOpenAPIDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={async (spec) => {
          const res = await importFromOpenAPI({ spec })
          if (res.error) { toast.error(res.error.message); return }
          const count = res.data?.importEventTypesFromOpenAPI?.length ?? 0
          toast.success(`Imported ${count} event type(s)`)
          setImportOpen(false)
          reexecute({ requestPolicy: "network-only" })
        }}
      />
    </div>
  )
}

function ImportOpenAPIDialog({
  open,
  onOpenChange,
  onImport,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImport: (spec: string) => Promise<void>
}) {
  const [spec, setSpec] = useState("")
  const [loading, setLoading] = useState(false)
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setSpec("")
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result
      if (typeof text === "string") setSpec(text)
    }
    reader.readAsText(file)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import from OpenAPI Spec</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Upload OpenAPI JSON file</Label>
            <Input
              type="file"
              accept=".json,.yaml,.yml"
              onChange={handleFileUpload}
              className="text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Or paste spec JSON</Label>
            <Textarea
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
              className="font-mono text-xs min-h-48"
              placeholder='{"openapi": "3.1.0", "webhooks": {...}}'
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              disabled={!spec.trim() || loading}
              onClick={async () => {
                setLoading(true)
                try { await onImport(spec) } finally { setLoading(false) }
              }}
            >
              {loading ? "Importing..." : "Import"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function SchemaFormDialog({
  open,
  onOpenChange,
  title,
  initial,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  initial?: EventTypeSchemaFormValues
  onSubmit: (values: EventTypeSchemaFormValues) => Promise<void>
}) {
  const form = useForm<EventTypeSchemaFormValues>({
    resolver: zodResolver(eventTypeSchemaFormSchema),
    defaultValues: initial ?? { eventType: "", description: "", schema: "", example: "" },
  })

  useEffect(() => {
    if (open) {
      form.reset(initial ?? { eventType: "", description: "", schema: "", example: "" })
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="eventType">Event Type</Label>
            <Input
              id="eventType"
              className="font-mono"
              placeholder="invoice.paid"
              {...form.register("eventType")}
            />
            {form.formState.errors.eventType && (
              <p className="text-xs text-destructive">{form.formState.errors.eventType.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Describe what this event type represents"
              {...form.register("description")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="schema">JSON Schema (optional)</Label>
            <Textarea
              id="schema"
              className="font-mono text-xs min-h-24"
              placeholder='{"type": "object", "properties": {...}}'
              {...form.register("schema")}
            />
            {form.formState.errors.schema && (
              <p className="text-xs text-destructive">{form.formState.errors.schema.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="example">Example Payload (optional)</Label>
            <Textarea
              id="example"
              className="font-mono text-xs min-h-24"
              placeholder='{"invoice_id": "inv_123", "amount": 9900}'
              {...form.register("example")}
            />
            {form.formState.errors.example && (
              <p className="text-xs text-destructive">{form.formState.errors.example.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "..." : initial ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
